/** @jsxImportSource @emotion/react */
import { ComponentType, FC } from 'react';
import { Box } from '@mui/material';
import { ProgressBar } from 'components/sign-up';
import { useMerchantApplicationContext } from 'contexts';
import {
  activationStepsConfig,
  getActivationStepDescription,
  getActivationStepTitle,
  isActivationStepKey,
} from 'modules/activation/ActivationConfig';
import { StepComponentNavProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import * as styles from 'modules/activation/ActivationFlow.styles';

export interface WithActivationCommonLayoutOptions {
  showPageWrapper?: boolean;
  showTitle?: boolean;
  showDescription?: boolean;
  showProgressBar?: boolean;
  showNavButtons?: boolean;
}

export function withActivationCommonLayout<P extends Record<string, any>>(
  WrappedComponent: ComponentType<P>,
  options?: WithActivationCommonLayoutOptions
): FC<P & StepComponentNavProps> {
  const {
    showPageWrapper = true,
    showTitle = true,
    showDescription = true,
    showProgressBar = false, // MC-4775 Once progress bar is not hardcoded at Business, Director and Settlement details, change this back to true
    showNavButtons = true,
  } = options ?? {};

  const WithActivationCommonLayout: FC<P & StepComponentNavProps> = (props) => {
    const { activationStepState } = useMerchantApplicationContext();
    const activeStepKey = isActivationStepKey(activationStepState.activeStepKey)
      ? activationStepState.activeStepKey
      : activationStepsConfig[0].key;
    const stepTitle = getActivationStepTitle(activeStepKey);
    const stepDescription = getActivationStepDescription(activeStepKey);

    const {
      canGoBack,
      canContinue,
      isSubmitting,
      onBack,
      onContinue,
      ...wrappedProps
    } = props as P & StepComponentNavProps;

    if (!showPageWrapper) {
      return (
        <WrappedComponent
          {...(wrappedProps as any)}
          canGoBack={canGoBack}
          canContinue={canContinue}
          isSubmitting={isSubmitting}
          onBack={onBack}
          onContinue={onContinue}
        />
      );
    }

    return (
      <Box css={styles.pageWrapper}>
        <Box css={styles.contentPanel}>
          {showTitle && stepTitle && <h1 className="heading1">{stepTitle}</h1>}
          {showDescription && stepDescription && (
            <p className="body4" css={styles.stepDescription}>
              {stepDescription}
            </p>
          )}

          {showProgressBar && (
            <ProgressBar
              steps={activationStepsConfig}
              activeStepKey={activeStepKey}
            />
          )}

          <WrappedComponent
            {...(wrappedProps as any)}
            canGoBack={canGoBack}
            canContinue={canContinue}
            isSubmitting={isSubmitting}
            onBack={onBack}
            onContinue={onContinue}
          />

          {showNavButtons && onContinue && (
            <div css={styles.stepOneActionRow}>
              {(canGoBack ?? false) && (
                <button
                  className="body1"
                  type="button"
                  css={styles.stepOneReturnButton}
                  onClick={onBack ?? (() => {})}
                  disabled={isSubmitting ?? false}
                >
                  Back
                </button>
              )}
              <button
                className="body1"
                type="button"
                css={styles.stepOneNextButton}
                disabled={!(canContinue ?? true) || (isSubmitting ?? false)}
                onClick={onContinue}
              >
                Next
              </button>
            </div>
          )}
        </Box>
      </Box>
    );
  };

  const wrappedName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithActivationCommonLayout.displayName = `withActivationCommonLayout(${wrappedName})`;

  return WithActivationCommonLayout;
}
